import { createSlice } from "@reduxjs/toolkit";
import {
  AccountStatus,
  CardAccessType,
  CardStatus,
  OrganizationType,
  User,
  subscriptionCategory,
  subscriptionStandard
} from "src/types/commonTypes";
import { Wallet } from "./wallets/walletSlice";
import userColumns from "src/page/users/usersConfig";
import cardColumns from "src/page/cards/cardsConfig";
import organisationColumns from "src/components/orgListCard/listTable/orgListTable";
import internalUserColumns from "src/page/internalUser/internalUsersConfig";
import internalWalletColumns from "src/components/internalWalletList/internalwalletListTable/InternalWalletListTable";
import internalCardColumns from "src/page/internalcards/internalcardsConfig";
import internalOrganisationCoulmns from "src/components/internalOrgListCard/listTable/internalOrgListTable";
import internalFileColumns from "src/page/internalFile/internalFileConfig";
import cardSettingColumns from "src/page/cardSetting/cardSettingConfig";
import plansColumns from "src/page/plans/plansConfig";
import chlidrenPlansColumn from "src/page/childrenPlans/childrenPlansConfig";
import reportsColumn from "src/page/reports/reportsConfig";
import organisationReportsColumn from "src/page/organisationReports/organisationReportsConfig";
import groupColumn from "src/page/group/groupConfig";
import { reportContentColumns } from "src/page/reportsContent/reportsContentConfig";
import licenceReportsColumn from "src/page/licenceReports/licenceReportsConfig";
import recurringTransferColumns from "src/page/recurringTransfer/recurringTransferConfig";
import licenceReportsContentColumn from "src/page/licenceReportsContent/licenceReportsContentConfig";
import walletReportsColumn from "src/page/walletReports/walletReportsConfig";
import walletReportsContentColumn from "src/page/walletReportsContent/walletReportsContentConfig";
import internalReportsColumn from "src/page/internalReports/internalReportsConfig";
import expenseColumns from "src/page/expense/expenseConfig";
import orderColumns from "src/page/stripeTransactionHistory/orderConfig";
import internalOrderColumns from "src/page/internalOrder/internalOrderConfig";
import entitlementColumns from "src/page/entitlement/entitlementConfig";
import { Card } from "./cards/cardSlice";
import { Group } from "./group/groupSlice";
import type { EmbeddedReimbursementWallet } from "./approvals/slices/reimbursementSlice";
import walletColumns from "./wallets/walletsConfig";

export type ActionRequiredFilter = "RECEIPT_REQUIRED" | "APPROVAL_REQUIRED" | "VERIFICATION_REQUIRED" | null;
export type XeroMappingStateFilter = "BOTH_EXIST" | "BOTH_MISSING" | "ACCOUNT_EXIST" | "CATEGORY_EXIST" | null;

export const initialTransactionFilters: {
  wallet: Wallet[] | null;
  user: User | null;
  group: Group[] | null;
  startDate: string | null;
  endDate: string | null;
  minAmount: string | null;
  maxAmount: string | null;
  activityName: string | null;
  receiptAvailable: string | null;
  activityStatus: string | null;
  accountStatus: AccountStatus | null;
  ppan: string | null;
  xero_account_exist: boolean | null;
  xero_bank_transaction_exist: boolean | null;
  actionRequired: ActionRequiredFilter;
} = {
  wallet: null,
  user: null,
  group: null,
  startDate: null,
  endDate: null,
  minAmount: null,
  maxAmount: null,
  activityName: null,
  receiptAvailable: null,
  activityStatus: null,
  accountStatus: null,
  ppan: null,
  xero_account_exist: null,
  xero_bank_transaction_exist: null,
  actionRequired: null
};

const initialWalletFilters: {
  startDate: string | null;
  endDate: string | null;
  minAmount: string | null;
  maxAmount: string | null;
  status: string | null;
  type: string[] | null;
  user: User[] | null;
  sortBy: string | null;
} = {
  startDate: null,
  endDate: null,
  minAmount: null,
  maxAmount: null,
  status: "ACTIVE",
  type: ["GENERAL", "CASH", "GROUP", "SERVICE"],
  user: null,
  sortBy: null
};

const initialInternalWalletFilters: {
  startDate: string | null;
  endDate: string | null;
  minAmount: string | null;
  maxAmount: string | null;
  status: string | null;
  type: string[] | null;
  user: User[] | null;
  sortBy: string | null;
} = {
  startDate: null,
  endDate: null,
  minAmount: null,
  maxAmount: null,
  status: null,
  type: ["GENERAL", "CASH", "GROUP", "SERVICE"],
  user: null,
  sortBy: null
};

const initialCardFilters: {
  status: CardStatus | null;
  type: CardAccessType | null;
  wallet: Wallet[] | null;
  user: User[] | null;
} = {
  status: null,
  type: null,
  wallet: null,
  user: null
};

const initialNovattiFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};
const initialReportsFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};
const initialInternalReportsFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};
const initialOrganisationReportsFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};

const initialLicenceReportsFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};

const initialWalletReportsFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};

const initialBulkUploadFilters: {
  fileType: string[] | null;
  fileTypes: string[];
  processStatuses: string[];
  startDate: string | null;
  endDate: string | null;
  searchText: string;
} = {
  fileType: null,
  fileTypes: [],
  processStatuses: [],
  startDate: null,
  endDate: null,
  searchText: ""
};

const initialInternalBulkUploadFilters: {
  fileTypes: string[];
  processStatuses: string[];
  organisationIds: string[];
  startDate: string | null;
  endDate: string | null;
  searchText: string;
} = {
  fileTypes: [],
  processStatuses: [],
  organisationIds: [],
  startDate: null,
  endDate: null,
  searchText: ""
};

const initialCardSettingFilters: {
  status: string | null;
  wallet: Wallet | null;
  card: Card | null;
} = {
  status: "ACTIVE",
  wallet: null,
  card: null
};
const initialPlansFilters: {
  status: string | null;
  category: subscriptionCategory | null;
  type: OrganizationType | null;
  standard: subscriptionStandard | null;
} = {
  status: "ACTIVE",
  category: null,
  type: null,
  standard: null
};

const initialGroupFilters: {
  status: string | null;
} = {
  status: "ACTIVE"
};

const initialRecurringTransferFilters: {
  wallet: Wallet | null;
  type: string | null;
  status: string | null;
} = {
  wallet: null,
  type: null,
  status: "ACTIVE"
};

const initialExpenseFilters: {
  startDate: string | null;
  endDate: string | null;
} = {
  startDate: null,
  endDate: null
};

const initialXeroFilters: {
  mappingState: XeroMappingStateFilter;
} = {
  mappingState: null
};

export const initialReimbursementFilters: {
  sourceWallet: EmbeddedReimbursementWallet | null;
  destinationWallet: EmbeddedReimbursementWallet | null;
  requestedBy: User | null;
} = {
  sourceWallet: null,
  destinationWallet: null,
  requestedBy: null
};

const initialColumn: {
  transaction: string[] | null;
  wallet: string[];
  user: string[];
  card: string[];
  organisation: string[];
  internalUser: string[];
  internalWallet: string[];
  internalCard: string[];
  internalOrganisation: string[];
  internalFile: string[];
  internalFileContent: string[];
  bulkUpload: string[];
  internalBulkUpload: string[];
  bulkUploadDetail: string[];
  internalBulkUploadDetail: string[];
  cardSetting: string[];
  plans: string[];
  childrenPlans: string[];
  reports: string[];
  internalReports: string[];
  organisationReports: string[];
  reportsContent: string[];
  licenceReports: string[];
  licenceReportsContent: string[];
  walletReports: string[];
  walletReportsContent: string[];
  group: string[];
  recurringTransfer: string[];
  expense: string[];
  order: string[];
  internalOrder: string[];
  entitlement: string[];
} = {
  transaction: null,
  wallet: walletColumns().map((col) => col.key),
  // `true` keeps "Approver 2" in the default selection; the column itself only
  // renders for organisations that capture a second approver.
  user: userColumns(undefined, true).map((col) => col.key),
  card: cardColumns.map((col) => col.key),
  cardSetting: cardSettingColumns.map((col) => col.key),
  organisation: organisationColumns().map((col) => col.key),
  internalUser: internalUserColumns().map((col) => col.key),
  internalWallet: internalWalletColumns().map((col) => col.key),
  internalCard: internalCardColumns.map((col) => col.key),
  internalOrganisation: internalOrganisationCoulmns.map((col) => col.key),
  internalFile: internalFileColumns.map((col) => col.key),
  internalFileContent: [],
  bulkUpload: [],
  internalBulkUpload: [],
  bulkUploadDetail: [],
  internalBulkUploadDetail: [],
  plans: plansColumns.map((col) => col.key),
  childrenPlans: chlidrenPlansColumn.map((col) => col.key),
  reports: reportsColumn.map((col) => col.key),
  organisationReports: organisationReportsColumn.map((col) => col.key),
  internalReports: internalReportsColumn.map((col) => col.key),
  reportsContent: reportContentColumns.map((col) => col.key),
  licenceReports: licenceReportsColumn.map((col) => col.key),
  licenceReportsContent: licenceReportsContentColumn.map((col) => col.key),
  walletReports: walletReportsColumn.map((col) => col.key),
  walletReportsContent: walletReportsContentColumn.map((col) => col.key),
  group: groupColumn.map((col) => col.key),
  recurringTransfer: recurringTransferColumns.map((col) => col.key),
  expense: expenseColumns.map((col) => col.key),
  order: orderColumns.map((col) => col.key),
  internalOrder: internalOrderColumns.map((col) => col.key),
  entitlement: entitlementColumns().map((col) => col.key)
};

const initialState = {
  transaction: initialTransactionFilters,
  wallet: initialWalletFilters,
  internalWallet: initialInternalWalletFilters,
  card: initialCardFilters,
  novatti: initialNovattiFilters,
  bulkUpload: initialBulkUploadFilters,
  internalBulkUpload: initialInternalBulkUploadFilters,
  cardSetting: initialCardSettingFilters,
  plans: initialPlansFilters,
  reports: initialReportsFilters,
  internalReports: initialInternalReportsFilters,
  organisationReports: initialOrganisationReportsFilters,
  licenceReports: initialLicenceReportsFilters,
  walletReports: initialWalletReportsFilters,
  group: initialGroupFilters,
  recurringTransfer: initialRecurringTransferFilters,
  expense: initialExpenseFilters,
  xero: initialXeroFilters,
  reimbursement: initialReimbursementFilters,
  column: initialColumn
};

const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    resetFilters: () => {
      return { ...initialState };
    },
    setTransactionFilter: (state, action) => {
      state.transaction = { ...state.transaction, ...action.payload };
    },
    resetTransactionFilters: (state) => {
      state.transaction = initialTransactionFilters;
    },
    setWalletFilters: (state, action) => {
      state.wallet = { ...state.wallet, ...action.payload };
    },
    resetWalletFilters: (state) => {
      state.wallet = initialWalletFilters;
    },
    setInternalWalletFilters: (state, action) => {
      state.internalWallet = { ...state.internalWallet, ...action.payload };
    },
    resetInternalWalletFilters: (state) => {
      state.internalWallet = initialInternalWalletFilters;
    },
    setCardFilters: (state, action) => {
      state.card = { ...state.card, ...action.payload };
    },
    resetCardFilters: (state) => {
      state.card = initialCardFilters;
    },
    setNovattiFilter: (state, action) => {
      state.novatti = { ...state.novatti, ...action.payload };
    },
    setReportsFilter: (state, action) => {
      state.reports = { ...state.reports, ...action.payload };
    },
    setInternalReportsFilter: (state, action) => {
      state.internalReports = { ...state.internalReports, ...action.payload };
    },
    setOrganisationReportsFilter: (state, action) => {
      state.organisationReports = { ...state.organisationReports, ...action.payload };
    },
    resetInternalReportsFilters: (state) => {
      state.internalReports = initialInternalReportsFilters;
    },
    resetOrganisationReportsFilters: (state) => {
      state.organisationReports = initialOrganisationReportsFilters;
    },
    setLicenceReportsFilter: (state, action) => {
      state.licenceReports = { ...state.licenceReports, ...action.payload };
    },
    resetLicenceReportsFilters: (state) => {
      state.licenceReports = initialLicenceReportsFilters;
    },
    setWalletReportsFilter: (state, action) => {
      state.walletReports = { ...state.walletReports, ...action.payload };
    },
    resetWalletReportsFilters: (state) => {
      state.walletReports = initialWalletReportsFilters;
    },
    setBulkUploadFilter: (state, action) => {
      state.bulkUpload = { ...state.bulkUpload, ...action.payload };
    },
    resetBulkUploadFilter: (state) => {
      state.bulkUpload = initialBulkUploadFilters;
    },
    setInternalBulkUploadFilter: (state, action) => {
      state.internalBulkUpload = { ...state.internalBulkUpload, ...action.payload };
    },
    resetInternalBulkUploadFilter: (state) => {
      state.internalBulkUpload = initialInternalBulkUploadFilters;
    },
    resetNovattiFilters: (state) => {
      state.novatti = initialNovattiFilters;
    },
    resetReportsFilters: (state) => {
      state.novatti = initialNovattiFilters;
    },
    setCardSettingFilters: (state, action) => {
      state.cardSetting = { ...state.cardSetting, ...action.payload };
    },
    resetCardSettingFilters: (state) => {
      state.cardSetting = initialCardSettingFilters;
    },
    setPlansFilters: (state, action) => {
      state.plans = { ...state.plans, ...action.payload };
    },
    resetPlansFilters: (state) => {
      state.plans = initialPlansFilters;
    },
    setGroupFilters: (state, action) => {
      state.group = { ...state.group, ...action.payload };
    },
    resetGroupFilters: (state) => {
      state.group = initialGroupFilters;
    },
    setRecurringTransferFilters: (state, action) => {
      state.recurringTransfer = { ...state.recurringTransfer, ...action.payload };
    },
    resetRecurringTransferFilters: (state) => {
      state.recurringTransfer = initialRecurringTransferFilters;
    },
    setExpenseFilters: (state, action) => {
      state.expense = { ...state.expense, ...action.payload };
    },
    resetExpenseFilters: (state) => {
      state.expense = initialExpenseFilters;
    },
    setXeroFilters: (state, action) => {
      state.xero = { ...state.xero, ...action.payload };
    },
    resetXeroFilters: (state) => {
      state.xero = initialXeroFilters;
    },
    setReimbursementFilters: (state, action) => {
      state.reimbursement = { ...state.reimbursement, ...action.payload };
    },
    resetReimbursementFilters: (state) => {
      state.reimbursement = initialReimbursementFilters;
    },
    setColumnFilter: (state, action) => {
      state.column = {
        ...state.column,
        ...action.payload
      };
    }
  }
});

export const {
  setTransactionFilter,
  resetFilters,
  resetTransactionFilters,
  setWalletFilters,
  resetWalletFilters,
  setInternalWalletFilters,
  resetInternalWalletFilters,
  setCardFilters,
  resetCardFilters,
  setNovattiFilter,
  resetNovattiFilters,
  setBulkUploadFilter,
  resetBulkUploadFilter,
  setInternalBulkUploadFilter,
  resetInternalBulkUploadFilter,
  setCardSettingFilters,
  resetCardSettingFilters,
  setPlansFilters,
  resetPlansFilters,
  setColumnFilter,
  setReportsFilter,
  resetReportsFilters,
  setInternalReportsFilter,
  resetInternalReportsFilters,
  setOrganisationReportsFilter,
  resetOrganisationReportsFilters,
  setLicenceReportsFilter,
  resetLicenceReportsFilters,
  setWalletReportsFilter,
  resetWalletReportsFilters,
  setGroupFilters,
  resetGroupFilters,
  setRecurringTransferFilters,
  resetRecurringTransferFilters,
  setExpenseFilters,
  resetExpenseFilters,
  setXeroFilters,
  resetXeroFilters,
  setReimbursementFilters,
  resetReimbursementFilters
} = filterSlice.actions;

export default filterSlice.reducer;
